import React from "react";

import Hidden from "@busbud/design-system-components/dist/Hidden";
import Label from "@busbud/design-system-components/dist/Label";
import type { ResponsiveSizeProp } from "@busbud/design-system-components/dist/Label/Label.types";
import Stack from "@busbud/design-system-components/dist/Stack";

import { useAppContext } from "@app/helpers/hooks";
import { getPassengerCount } from "@app/helpers/search-client";
import { useSeatSelectionFirst } from "@app/modules/seat-selection-first/hooks/use-seat-selection-first";
import type { TripOption } from "@app/types/results-types";
import type { SearchInfo } from "@app/types/search-types";

import {
  getLocalizedPrice,
  getLocalizedPriceWithoutSymbol
} from "../localized-price";

interface Props {
  disabled?: boolean;
  search: SearchInfo;
  selected_trip: TripOption;
  segment_id?: string;
}

export const PriceInformation: React.FC<Props> = ({
  disabled,
  selected_trip,
  search,
  segment_id
}) => {
  const { locale, intlProvider } = useAppContext();
  const { is_seat_selection_first_enabled } = useSeatSelectionFirst();

  const show_from_prefix = is_seat_selection_first_enabled && !!disabled;
  const passenger_count = getPassengerCount(search);
  // TODO: get view info from useNavigation hook
  const is_seat_selection_first_page = !!(
    is_seat_selection_first_enabled && segment_id
  );

  const price_in_cents = is_seat_selection_first_page
    ? // TODO ssf - some seats might have different price, this needs to be refined
      // @ts-expect-error TS2532 Unsafe indexed access (noUncheckedIndexedAccess); tracked in #24591
      selected_trip.prices_per_segment[segment_id].price_in_cents *
      passenger_count
    : selected_trip.price_in_cents;

  const localized_price = getLocalizedPrice(
    {
      nb_units: passenger_count,
      price_in_cents
    },
    { locale, intlProvider }
  );

  return (
    <div
      data-testid="trip-displayed-price"
      data-cy="trip-displayed-price"
      itemProp="offers"
      itemScope
      itemType="https://schema.org/Offer"
    >
      <Hidden mdUp>
        <PriceInformationContent
          Wrapper={({ children }) => (
            <Stack spacing={0.25} direction="column">
              {children}
            </Stack>
          )}
          disabled={!!disabled}
          localized_price={localized_price}
          size_from="xs"
          size_price="xl"
          show_from_prefix={show_from_prefix}
        />
      </Hidden>
      <Hidden smDown>
        <PriceInformationContent
          Wrapper={({ children }) => (
            <Stack spacing={0.5} direction="row">
              {children}
            </Stack>
          )}
          disabled={!!disabled}
          localized_price={localized_price}
          size_from="xxl"
          size_price="xxl"
          show_from_prefix={show_from_prefix}
        />
      </Hidden>
    </div>
  );
};

type ContentProps = {
  disabled: boolean;
  localized_price: string;
  size_from: ResponsiveSizeProp;
  size_price: ResponsiveSizeProp;
  show_from_prefix: boolean;
  Wrapper: React.ElementType;
};
const PriceInformationContent: React.FC<ContentProps> = ({
  disabled,
  localized_price,
  size_from,
  size_price,
  show_from_prefix,
  Wrapper
}) => {
  const { intlProvider, translator } = useAppContext();

  const localized_price_without_symbol =
    getLocalizedPriceWithoutSymbol(localized_price);

  const price_meta_props = localized_price_without_symbol
    ? {
        itemProp: "price",
        content: localized_price_without_symbol
      }
    : {};

  const text_props = {
    align: "right",
    fontWeight: "bold",
    color: disabled ? "text.quaternary.opaque" : "text.primary.opaque"
  } as const;

  return (
    <Wrapper>
      {!!show_from_prefix && (
        <Label size={size_from} {...text_props}>
          {translator.t("!results.classes-fares.from-price")}
        </Label>
      )}
      <Label
        data-testid="trip-displayed-localized-price"
        size={size_price}
        {...text_props}
        {...price_meta_props}
      >
        {localized_price}
      </Label>
      <meta
        data-testid="trip-displayed-price-currency"
        itemProp="priceCurrency"
        content={intlProvider.currency}
      />
    </Wrapper>
  );
};
